<script setup lang="ts">
import { StructuredText, renderNodeRule } from 'vue-datocms'
import { isLink, isThematicBreak } from 'datocms-structured-text-utils'

import {
  Button,
  ButtonBlock,
  NuxtLink,
  DatoLink,
  HorizontalRule,
  LazyGlb,
  LazyCollectionBlock,
  LazyFactsBlock,
  LazyFeaturesBlock,
  IframeBlock,
  LazyMediaBlock,
  LazyPressBlock,
  LazyPressesBlock,
  LazyPullQuoteBlock,
  LazyTableBlock,
  LazyTeaserBlock,
  LazyVideoBlock,
  LazyVideoExternalBlock
} from '#components'

defineProps<{
  data: ArticleModelBodyField | WorkModelBodyField | SectionStructuredTextModelStructuredTextField
}>()

const customNodeRules = [
  renderNodeRule(isLink, ({ node, key }) => h(DatoLink, { key, node })),
  renderNodeRule(isThematicBreak, ({ node, key }) => h(HorizontalRule, { key, node }))
]

const renderLinkToRecord = ({ record, children }: unknown) =>
  h(NuxtLink, { to: usePathByType(record._modelApiKey, record.slug) }, () => children)

// TODO: support for inline records (Testimonials)
const renderInlineRecord = ({ record }: unknown) => {
  return h(Button, { to: usePathByType(record._modelApiKey, record.slug) }, () => [record.title])
}

const renderBlock = ({
  record
}: {
  record:
    | ButtonRecord
    | CollectionBlockRecord
    | FactListRecord
    | FeatureListRecord
    | GlbRecord
    | IframeRecord
    | MediaBlockRecord
    | PressRecord
    | PressListRecord
    | PullQuoteRecord
    | TableRecord
    | TeaserBlockRecord
    | VideoRecord
    | VideoExternalRecord
}) => {
  switch (record.__typename) {
    case 'ButtonRecord':
      return h(ButtonBlock, {
        data: record
      })
    case 'CollectionBlockRecord':
      return h(LazyCollectionBlock, {
        data: record
      })
    case 'FactListRecord':
      return h(LazyFactsBlock, {
        data: record
      })
    case 'FeatureListRecord':
      return h(LazyFeaturesBlock, {
        data: record
      })
    case 'GlbRecord':
      return h(LazyGlb, {
        data: record
      })
    case 'IframeRecord':
      return h(IframeBlock, {
        data: record
      })
    case 'MediaBlockRecord':
      return h(LazyMediaBlock, {
        data: record
      })
    case 'PressRecord':
      return h(LazyPressBlock, {
        data: record
      })
    case 'PressListRecord':
      return h(LazyPressesBlock, {
        data: record
      })
    case 'PullQuoteRecord':
      return h(LazyPullQuoteBlock, {
        data: record
      })
    case 'TableRecord':
      return h(LazyTableBlock, {
        data: record
      })
    case 'TeaserBlockRecord':
      return h(LazyTeaserBlock, {
        data: record
      })
    case 'VideoRecord':
      return h(LazyVideoBlock, {
        data: record
      })
    case 'VideoExternalRecord':
      return h(LazyVideoExternalBlock, {
        data: record
      })
  }
}
</script>

<template>
  <StructuredText
    class="editor"
    :data="data"
    :custom-node-rules="customNodeRules"
    :render-block="renderBlock"
    :render-inline-record="renderInlineRecord"
    :render-link-to-record="renderLinkToRecord"
  />
</template>
